import { useEffect, useState } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import api from "@/lib/api";
import { BOOKING } from "@/constants/testIds";
import { useSettings } from "@/context/SettingsContext";
import useSEO from "@/lib/useSEO";

const FALLBACK_TYPES = [
  { name: "In studio" },
  { name: "A domicilio" },
  { name: "Video call" },
];

export default function Prenota() {
  useSEO({ title: "Prenota Consulenza", description: "Prenota una consulenza gratuita con Giovanna Matino, interior designer a Napoli: in studio, a domicilio o in video call." });
  const { settings } = useSettings();
  const rawTypes = settings.consultation_types?.length ? settings.consultation_types : FALLBACK_TYPES;
  const consultationTypes = rawTypes.map((t) => (typeof t === "string" ? { name: t } : { name: t.name }));
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [dayClosed, setDayClosed] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "", consultation_type: "In studio" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [privacy, setPrivacy] = useState(false);

  useEffect(() => {
    if (!date) { setSlots([]); setDayClosed(false); return; }
    const dateStr = format(date, "yyyy-MM-dd");
    setSelectedSlot("");
    api.get("/bookings/availability", { params: { date: dateStr } })
      .then((r) => { setSlots(r.data.slots); setDayClosed(!!r.data.closed); })
      .catch(() => { setSlots([]); setDayClosed(false); });
  }, [date]);

  const isDayDisabled = (d) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const min = new Date(today); min.setDate(min.getDate() + (settings.min_notice_days ?? 1));
    const max = new Date(today); max.setDate(max.getDate() + (settings.max_advance_days ?? 60));
    if (d < min || d > max) return true;
    if ((settings.availability_weekdays_off || []).includes(d.getDay())) return true;
    if ((settings.blocked_dates || []).includes(format(d, "yyyy-MM-dd"))) return true;
    return false;
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!date || !selectedSlot) {
      toast.error("Seleziona data e orario");
      return;
    }
    if (!privacy) {
      toast.error("Devi accettare l'informativa privacy per prenotare");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        ...form,
        date: format(date, "yyyy-MM-dd"),
        time_slot: selectedSlot,
        privacy: true,
      });
      toast.success("Prenotazione confermata! Controlla la tua e-mail.");
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", notes: "", consultation_type: "In studio" });
      setPrivacy(false);
      setSelectedSlot("");
      setDate(null);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Errore, riprova");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-prenota">
      <section className="container-x pt-10 pb-16">
        <span className="eyebrow">Prenota Consulenza</span>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] mt-6 max-w-4xl">
          Iniziamo con una <em className="text-gold font-light">conversazione.</em>
        </h1>
        <p className="mt-8 text-taupe max-w-2xl text-lg font-light">
          Un primo incontro gratuito e senza impegno per raccontarci il tuo progetto,
          capire le esigenze e definire i passi successivi.
        </p>
      </section>

      <section className="container-x pb-24">
        {success ? (
          <div className="bg-white border border-hair p-12 max-w-2xl mx-auto text-center">
            <div className="eyebrow">Prenotazione ricevuta</div>
            <h2 className="font-serif text-4xl mt-4">Grazie!</h2>
            <p className="text-taupe mt-6 font-light">
              Il tuo appuntamento è confermato. Ti abbiamo inviato un'e-mail con il riepilogo
              e il file da aggiungere al tuo calendario.
            </p>
            <button onClick={() => setSuccess(false)} className="btn-outline-ink mt-8" data-testid="booking-new">
              Nuova prenotazione
            </button>
          </div>
        ) : (
        <form onSubmit={onSubmit} className="grid lg:grid-cols-12 gap-10" data-testid={BOOKING.form}>
          <div className="lg:col-span-6 bg-white border border-hair p-8">
            <div className="eyebrow mb-4">1 · Scegli una data</div>
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              locale={it}
              disabled={isDayDisabled}
              className="rounded-none"
            />

            {date && dayClosed && (
              <p className="mt-8 text-sm text-taupe" data-testid="booking-day-closed">
                Lo studio non è disponibile in questa data. Scegli un altro giorno.
              </p>
            )}

            {date && !dayClosed && (
              <div className="mt-8">
                <div className="eyebrow mb-4">
                  2 · Orario per {format(date, "d MMMM yyyy", { locale: it })}
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {slots.map((s) => (
                    <button
                      type="button"
                      key={s.time}
                      onClick={() => s.available && setSelectedSlot(s.time)}
                      disabled={!s.available}
                      data-testid={BOOKING.slot(s.time)}
                      className={`py-3 text-sm border transition-all ${
                        selectedSlot === s.time
                          ? "bg-ink text-bone border-ink"
                          : s.available
                            ? "border-hair hover:border-ink"
                            : "border-hair text-taupe/40 line-through cursor-not-allowed"
                      }`}
                    >
                      {s.time}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-6 bg-white border border-hair p-8 space-y-6">
            <div className="eyebrow">3 · I tuoi dati</div>

            <div>
              <Label className="text-xs tracking-[0.14em] uppercase">Nome e Cognome</Label>
              <Input required value={form.name} onChange={set("name")} data-testid={BOOKING.name} className="rounded-none mt-2" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs tracking-[0.14em] uppercase">Email</Label>
                <Input type="email" required value={form.email} onChange={set("email")} data-testid={BOOKING.email} className="rounded-none mt-2" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.14em] uppercase">Telefono</Label>
                <Input required value={form.phone} onChange={set("phone")} data-testid={BOOKING.phone} className="rounded-none mt-2" />
              </div>
            </div>

            <div>
              <Label className="text-xs tracking-[0.14em] uppercase">Tipologia consulenza</Label>
              <RadioGroup
                value={form.consultation_type}
                onValueChange={(v) => setForm({ ...form, consultation_type: v })}
                className="mt-3 grid grid-cols-3 gap-2"
                data-testid={BOOKING.type}
              >
                {consultationTypes.map((t) => (
                  <label key={t.name} className={`border p-3 text-center text-sm cursor-pointer transition-colors ${form.consultation_type === t.name ? "bg-ink text-bone border-ink" : "border-hair hover:border-ink"}`}>
                    <RadioGroupItem value={t.name} className="sr-only" />
                    <span className="block">{t.name}</span>
                    <span className={`block text-[11px] mt-1 ${form.consultation_type === t.name ? "text-bone/70" : "text-taupe"}`}>
                      Gratuita
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label className="text-xs tracking-[0.14em] uppercase">Note (facoltative)</Label>
              <Textarea rows={4} value={form.notes} onChange={set("notes")} data-testid={BOOKING.notes} className="rounded-none mt-2" />
            </div>

            <div>
              <label className="flex items-start gap-3 cursor-pointer min-h-[44px]" data-testid="booking-privacy-wrap">
                <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)}
                  className="w-5 h-5 mt-0.5 shrink-0 accent-[#9E8462]" data-testid="booking-privacy" />
                <span className="text-xs text-taupe leading-relaxed">
                  Dichiaro di aver letto la <a href="/privacy" target="_blank" rel="noopener" className="underline hover:text-ink">informativa privacy</a> e
                  acconsento al trattamento dei miei dati per la gestione della prenotazione. <span className="text-ink">*</span>
                </span>
              </label>
            </div>

            <button type="submit" disabled={submitting} className="btn-ink w-full disabled:opacity-60" data-testid={BOOKING.submit}>
              {submitting ? "Invio in corso…" : "Conferma Prenotazione Gratuita"}
            </button>
          </div>
        </form>
        )}
      </section>
    </div>
  );
}
